import { useId } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Checkmark } from '@remarkable/ark-icons';
import {
  ErrorMessage,
  Field,
  Input,
  Label,
  Typography,
} from '@remarkable/ark-web';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { HTTPError } from 'src/api/createApiClient.types';
import { Button } from 'src/components';

import type { CheckoutPromotion } from './useCheckoutPromotion';

type PromotionCodeInputProps = {
  promotion: CheckoutPromotion;
  onApply: () => void | Promise<void>;
  isValidatingAddress?: boolean;
};

export function PromotionCodeInput({
  promotion,
  onApply,
  isValidatingAddress = false,
}: PromotionCodeInputProps) {
  const { t } = useLingui();
  const inputId = useId();
  const isPending = promotion.isPending || isValidatingAddress;
  const isApplied = !!promotion.appliedPromotion;
  const errorMessage = promotion.error
    ? promotion.error instanceof HTTPError &&
      promotion.error.response.status === 400
      ? t`We couldn't apply this promo code. Check the code and billing address, then try again.`
      : t`We couldn't check this promo code. Please try again.`
    : undefined;

  return (
    <>
      <Typography variant="interface-lg" fontWeight="semibold" leadingNone>
        <Trans>I have a promo code</Trans>
      </Typography>

      <div className="flex flex-col gap-8">
        <div className="flex flex-wrap items-center gap-16">
          <Field className="w-[18rem] max-w-full min-w-0">
            <Label htmlFor={inputId} className="sr-only">
              <Trans>Add promotion code</Trans>
            </Label>
            <div className="relative">
              <Input
                id={inputId}
                type="text"
                className={isApplied ? 'w-full pr-48' : 'w-full'}
                placeholder={t`Add promotion code`}
                autoComplete="off"
                value={promotion.code}
                hasError={!!errorMessage}
                aria-invalid={!!errorMessage}
                aria-describedby={errorMessage ? `${inputId}-error` : undefined}
                onChange={(event) => promotion.setCode(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
              {isApplied && (
                <Checkmark
                  size={24}
                  aria-hidden
                  data-cy="promotion-applied-icon"
                  className="pointer-events-none absolute top-1/2 right-12 -translate-y-1/2 text-feedback-green-500"
                />
              )}
            </div>
          </Field>
          {isApplied ? (
            <Button
              type="button"
              data-cy="promotion-remove"
              variant="tertiary-neutral"
              className="ml-8"
              onClick={() => {
                tracker.trackEvent(
                  new ButtonClicked({
                    component_location:
                      ComponentLocations.CHECKOUT.CONNECT_CHECKOUT,
                    text: 'Remove',
                    action: 'remove promotion code',
                  })
                );
                promotion.setCode('');
              }}
            >
              <Trans>Remove</Trans>
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              loading={isPending}
              disabled={!promotion.hasCode || isPending}
              onClick={onApply}
            >
              <Trans>Apply</Trans>
            </Button>
          )}
        </div>
        {errorMessage && (
          <ErrorMessage id={`${inputId}-error`} role="alert">
            {errorMessage}
          </ErrorMessage>
        )}
      </div>
    </>
  );
}
